import React from 'react';
import { motion } from 'framer-motion';
import { Scale, ArrowLeft, Building2, FileText, MapPin, Mail, Phone, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

const LegalNoticePage = () => {
  const lastUpdate = "19 de febrero de 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al inicio
          </Link>
          
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <Scale className="w-8 h-8 text-purple-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Aviso Legal</h1>
              <p className="text-slate-400">Última actualización: {lastUpdate}</p>
            </div>
          </div>
        </motion.div>

        {/* Important Disclaimer Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-400 mb-1">Disclaimer Importante</h3>
              <p className="text-red-200 text-sm">
                NotaryOS es una plataforma tecnológica de asistencia para la creación de documentos legales. 
                <strong> NO constituye un estudio jurídico, no provee asesoría legal personalizada, y no 
                reemplaza la consulta con un abogado colegiado.</strong> Los documentos generados son 
                plantillas que deben ser revisadas por un profesional del derecho antes de su uso en 
                situaciones legales complejas.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700 space-y-8"
        >
          {/* Información de la Empresa */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Información de la Empresa</h2>
            <div className="bg-slate-700/50 rounded-xl p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <Building2 className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-slate-400 text-sm">Razón Social</p>
                    <p className="text-white font-medium">NotaryOS SpA</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-slate-400 text-sm">RUT</p>
                    <p className="text-white font-medium">[Por configurar]</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-slate-400 text-sm">Domicilio Legal</p>
                    <p className="text-white font-medium">Santiago, Región Metropolitana, Chile</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="w-5 h-5 text-purple-400 mt-0.5" />
                  <div>
                    <p className="text-slate-400 text-sm">Correo Electrónico</p>
                    <p className="text-white font-medium">legal@notaryos.cl</p>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-slate-600">
                <p className="text-slate-300 text-sm">
                  NotaryOS SpA es una sociedad por acciones constituida conforme a las leyes de la 
                  República de Chile, inscrita en el Registro de Comercio.
                </p>
              </div>
            </div>
          </section>

          {/* Objeto del Sitio */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Objeto del Sitio Web</h2>
            <p className="text-slate-300 leading-relaxed">
              NotaryOS ofrece una plataforma tecnológica que facilita:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4 mt-3">
              <li>Generación asistida de documentos legales mediante inteligencia artificial</li>
              <li>Análisis de riesgo y detección de cláusulas problemáticas en contratos</li>
              <li>Gestión y almacenamiento seguro de documentos legales</li>
              <li>Firma digital de contratos entre múltiples partes</li>
              <li>Herramientas de traducción de lenguaje jurídico a lenguaje común</li>
              <li>Acceso a biblioteca de plantillas de contratos chilenos</li>
            </ul>
          </section>

          {/* Naturaleza del Servicio */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Naturaleza del Servicio</h2>
            <div className="space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <h3 className="font-semibold text-amber-400 mb-2">Lo que NotaryOS ES:</h3>
                <ul className="text-amber-200 text-sm space-y-1">
                  <li>• Una herramienta tecnológica de asistencia en la creación de documentos</li>
                  <li>• Un sistema de gestión documental con funciones de análisis</li>
                  <li>• Una plataforma de firma electrónica simple</li>
                  <li>• Un repositorio de plantillas legales de referencia</li>
                </ul>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <h3 className="font-semibold text-red-400 mb-2">Lo que NotaryOS NO ES:</h3>
                <ul className="text-red-200 text-sm space-y-1">
                  <li>• No es un estudio jurídico ni provee servicios legales</li>
                  <li>• No reemplaza la asesoría de un abogado colegiado</li>
                  <li>• No es una notaría ni tiene facultades notariales</li>
                  <li>• No emite documentos con fe pública</li>
                  <li>• No garantiza la validez legal de los documentos en todos los casos</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Limitación de Responsabilidad */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Limitación de Responsabilidad</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              De conformidad con la legislación chilena aplicable, NotaryOS SpA declara que:
            </p>
            <div className="bg-slate-700/50 rounded-xl p-4 space-y-3">
              <p className="text-slate-300 text-sm">
                <strong>4.1.</strong> Los documentos generados a través de la plataforma son plantillas 
                de referencia que pueden requerir adaptaciones según las circunstancias específicas de 
                cada caso.
              </p>
              <p className="text-slate-300 text-sm">
                <strong>4.2.</strong> NotaryOS no se hace responsable por las consecuencias derivadas 
                del uso de los documentos generados sin la debida revisión por un profesional del derecho.
              </p>
              <p className="text-slate-300 text-sm">
                <strong>4.3.</strong> El análisis de riesgo y las recomendaciones de la IA son orientativas 
                y no constituyen opinión legal vinculante.
              </p>
              <p className="text-slate-300 text-sm">
                <strong>4.4.</strong> La firma electrónica provista por la plataforma es de tipo simple, 
                conforme a la Ley 19.799, y puede no ser válida para todos los actos jurídicos.
              </p>
              <p className="text-slate-300 text-sm">
                <strong>4.5.</strong> NotaryOS no garantiza la disponibilidad ininterrumpida del servicio 
                ni se responsabiliza por pérdidas derivadas de interrupciones técnicas.
              </p>
            </div>
          </section>

          {/* Propiedad Intelectual */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Propiedad Intelectual</h2>
            <p className="text-slate-300 leading-relaxed">
              Todos los contenidos del sitio web, incluyendo pero no limitado a textos, gráficos, 
              logotipos, iconos, imágenes, clips de audio, descargas digitales, compilaciones de datos 
              y software, son propiedad de NotaryOS SpA o de sus proveedores de contenido y están 
              protegidos por las leyes chilenas e internacionales de propiedad intelectual.
            </p>
            <p className="text-slate-300 leading-relaxed mt-3">
              Los usuarios conservan todos los derechos sobre los documentos que crean utilizando 
              la plataforma. NotaryOS solo obtiene una licencia limitada para procesar y almacenar 
              dichos documentos según sea necesario para proveer el servicio.
            </p>
          </section>

          {/* Ley Aplicable */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Legislación Aplicable</h2>
            <p className="text-slate-300 leading-relaxed">
              Este sitio web y todos los servicios ofrecidos a través del mismo se rigen por las 
              leyes de la República de Chile, incluyendo pero no limitado a:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4 mt-3">
              <li><strong>Ley N° 19.628</strong> sobre Protección de la Vida Privada</li>
              <li><strong>Ley N° 19.496</strong> sobre Protección de los Derechos de los Consumidores</li>
              <li><strong>Ley N° 19.799</strong> sobre Documentos Electrónicos, Firma Electrónica y 
                  Servicios de Certificación</li>
              <li><strong>Código Civil</strong> de la República de Chile</li>
              <li><strong>Código de Comercio</strong> en lo pertinente</li>
            </ul>
          </section>

          {/* Jurisdicción */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Jurisdicción y Competencia</h2>
            <p className="text-slate-300 leading-relaxed">
              Para cualquier controversia que pudiera derivarse del acceso o uso de este sitio web, 
              las partes se someten expresamente a los Tribunales Ordinarios de Justicia de la ciudad 
              de Santiago de Chile, renunciando a cualquier otro fuero que pudiera corresponderles 
              en razón de su domicilio presente o futuro.
            </p>
          </section>

          {/* Modificaciones */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Modificaciones</h2>
            <p className="text-slate-300 leading-relaxed">
              NotaryOS SpA se reserva el derecho de modificar en cualquier momento y sin previo aviso 
              la presentación, configuración y contenido del sitio web, así como las condiciones 
              requeridas para su acceso y/o uso. El acceso y uso del sitio web después de dichos 
              cambios implica la aceptación de los mismos.
            </p>
          </section>

          {/* Contacto */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Contacto Legal</h2>
            <p className="text-slate-300">
              Para consultas legales o notificaciones judiciales:
            </p>
            <div className="mt-4 bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
              <div className="space-y-2 text-purple-300">
                <p><strong>Razón Social:</strong> NotaryOS SpA</p>
                <p><strong>Domicilio:</strong> Santiago, Chile</p>
                <p><strong>Email Legal:</strong> legal@notaryos.cl</p>
                <p><strong>Email Soporte:</strong> soporte@notaryos.cl</p>
              </div>
            </div>
          </section>
        </motion.div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          © 2026 NotaryOS SpA. Todos los derechos reservados.
        </div>
      </div>
    </div>
  );
};

export default LegalNoticePage;
